import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonIcon from '@mui/icons-material/Person';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import { createTransportCompany, createPilotProfile } from '../api/services';
import { useAuthentication } from '../store/AppContext';
import { DESIGN_TOKENS } from '../styles/theme';
import { SectionHeader, ErrorDisplay, LocationPickerMap, LoadingSpinner } from '../components/common';
import { geocode, reverseGeocode } from '../utils/geocoding';
import {
  hasNonEmptyValue,
  hasValidCoordinatePair,
  isValidEmail,
  isValidIban,
  isValidNip,
  isValidPolishPhoneNumber,
  isValidRegon,
  isValidWebsiteUrl,
  normalizeIban,
} from '../utils';
import type { Permission } from '../types';
import { useI18n } from '../i18n';

type LocationFormData = {
  city: string;
  lat: string;
  lng: string;
};

function useLocationFields<T extends LocationFormData>(
  formData: T,
  setFormData: React.Dispatch<React.SetStateAction<T>>
) {
  const isInternalUpdate = React.useRef(false);

  React.useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    const timer = window.setTimeout(async () => {
      if (!formData.city) return;

      const result = await geocode(formData.city);
      if (result) {
        isInternalUpdate.current = true;
        setFormData((prev) => ({
          ...prev,
          lat: result.lat.toString(),
          lng: result.lng.toString(),
        }));
      }
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [formData.city, setFormData]);

  React.useEffect(() => {
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    const timer = window.setTimeout(async () => {
      const lat = parseFloat(formData.lat);
      const lng = parseFloat(formData.lng);

      if (!Number.isNaN(lat) && !Number.isNaN(lng) && lat !== 0 && lng !== 0) {
        const result = await reverseGeocode(lat, lng);
        if (result?.city) {
          isInternalUpdate.current = true;
          setFormData((prev) => ({
            ...prev,
            city: result.city || prev.city,
          }));
        }
      }
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [formData.lat, formData.lng, setFormData]);

  return React.useCallback(
    async (lat: number, lng: number) => {
      isInternalUpdate.current = true;
      setFormData((prev) => ({
        ...prev,
        lat: lat.toString(),
        lng: lng.toString(),
      }));

      const result = await reverseGeocode(lat, lng);
      if (result?.city) {
        isInternalUpdate.current = true;
        setFormData((prev) => ({
          ...prev,
          city: result.city || '',
        }));
      }
    },
    [setFormData]
  );
}

function validateCompanyProfileForm(formData: {
  company_name: string;
  nip: string;
  regon: string;
  email: string;
  phone: string;
  website: string;
  lat: string;
  lng: string;
}, t: (key: string, params?: Record<string, string | number>) => string, options?: { requireCompanyName?: boolean }) {
  if (options?.requireCompanyName !== false && !hasNonEmptyValue(formData.company_name)) {
    return t('onboarding.enterCompanyName');
  }

  if (!isValidNip(formData.nip.trim())) {
    return t('onboarding.invalidNip');
  }

  if (!hasNonEmptyValue(formData.regon) || !isValidRegon(formData.regon.trim())) {
    return t('onboarding.invalidRegon');
  }

  if (!isValidEmail(formData.email.trim())) {
    return t('onboarding.invalidEmail');
  }

  if (hasNonEmptyValue(formData.phone) && !isValidPolishPhoneNumber(formData.phone.trim())) {
    return t('onboarding.invalidPhone');
  }

  if (!isValidWebsiteUrl(formData.website.trim())) {
    return t('onboarding.invalidWebsite');
  }

  if (!hasValidCoordinatePair(formData.lat, formData.lng)) {
    return t('onboarding.invalidCoordinates');
  }

  return null;
}

// ============================================================
// FIRST STEPS PAGE
// ============================================================

export function AlreadyRegistered() {
  const navigate = useNavigate();
  const { t } = useI18n();
  return (
    <Box
      sx={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        px: 2,
      }}
    >
      <Box sx={{ textAlign: 'center', maxWidth: 600 }}>
        <SentimentDissatisfiedIcon sx={{ fontSize: 56, color: DESIGN_TOKENS.colors.primaryLight, mb: 2 }} />
        <Typography variant="h3" sx={{ mb: 1.5 }}>
          {t('onboarding.alreadyRegisteredTitle')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {t('onboarding.alreadyRegisteredDescription')}
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/account')}
          size="large"
        >
          {t('onboarding.goToDashboard')}
        </Button>
      </Box>
    </Box>
  );
}

export function FirstStepsPage() {
  const { t } = useI18n();
  const { currentUser, isAuthenticated, isLoading } = useAuthentication();

  if (isLoading || (isAuthenticated && !currentUser)) {
    return <LoadingSpinner message={t('auth.checkingAccount')} fullHeight />;
  }

  if (currentUser?.membership) {
    return <AlreadyRegistered />;
  }

  return (
    <Box sx={{ backgroundColor: DESIGN_TOKENS.colors.background, minHeight: '80vh', py: 8 }}>
      <Container maxWidth="md">
        <SectionHeader
          title={t('onboarding.firstStepsTitle')}
          subtitle={t('onboarding.firstStepsSubtitle')}
          centered
        />

        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6}>
            <OnboardingTile
              icon={<LocalShippingIcon sx={{ fontSize: 48 }} />}
              title={t('onboarding.companyTileTitle')}
              description={t('onboarding.companyTileDescription')}
              path="/onboarding/company"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <OnboardingTile
              icon={<PersonIcon sx={{ fontSize: 48 }} />}
              title={t('onboarding.pilotTileTitle')}
              description={t('onboarding.pilotTileDescription')}
              path="/onboarding/pilot"
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

function OnboardingTile({ icon, title, description, path }: { icon: any, title: string, description: string, path: string }) {
  const { t } = useI18n();
  return (
    <Card
      component={RouterLink}
      to={path}
      sx={{
        height: '100%',
        textDecoration: 'none',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: DESIGN_TOKENS.shadows.cardHover,
          '& .arrow-icon': { transform: 'translateX(4px)' }
        }
      }}
    >
      <CardContent sx={{ p: 4, textAlign: 'center' }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: DESIGN_TOKENS.colors.primaryLighter,
            color: DESIGN_TOKENS.colors.primary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3
          }}
        >
          {icon}
        </Box>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {description}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: DESIGN_TOKENS.colors.primary, fontWeight: 700 }}>
          {t('onboarding.letsStart')} <ArrowForwardIcon className="arrow-icon" sx={{ ml: 1, transition: 'transform 0.2s' }} />
        </Box>
      </CardContent>
    </Card>
  );
}

// ============================================================
// COMPANY ONBOARDING PAGE
// ============================================================

export function CompanyOnboardingPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { currentUser, isAuthenticated, isLoading, refreshUser } = useAuthentication();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    company_name: '',
    description: '',
    nip: '',
    regon: '',
    email: currentUser?.email || '',
    phone: '',
    website: '',
    city: '',
    address: '',
    lat: '',
    lng: '',
  });

  const handleLocationSelect = useLocationFields(formData, setFormData);

  if (isLoading || (isAuthenticated && !currentUser)) {
    return <LoadingSpinner message={t('auth.checkingAccount')} fullHeight />;
  }

  if (currentUser?.membership) {
    return <AlreadyRegistered />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateCompanyProfileForm(formData, t);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const payload = {
        company_information: {
            company_data: {
              company_name: formData.company_name.trim(),
              description: formData.description.trim(),
            },
          tax_information: {
            tax_identification_number: formData.nip.trim(),
            regon: formData.regon.trim(),
          },
          contact_details: {
            email: formData.email.trim(),
            phone_number: formData.phone.trim(),
            website_url: formData.website.trim(),
            location: {
              city: formData.city.trim(),
              address: formData.address.trim(),
              lat: formData.lat ? parseFloat(formData.lat) : undefined,
              lng: formData.lng ? parseFloat(formData.lng) : undefined,
            }
          },
          verified: false,
          highlighted: false,
        },
        active: true,
      };

      await createTransportCompany(payload);

      await refreshUser();
      navigate('/account');
    } catch (err: any) {
      setError(err.message || t('onboarding.companyProfileError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Typography variant="h3" gutterBottom fontWeight={700}>{t('onboarding.companyTitle')}</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>{t('onboarding.companySubtitle')}</Typography>

      {error && (
        <Box sx={{ mb: 3 }}>
          <ErrorDisplay message={error} />
        </Box>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>{t('onboarding.companySection')}</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField fullWidth label={t('forms.companyName')} name="company_name" required value={formData.company_name} onChange={handleChange} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label={t('forms.description')} name="description" multiline rows={4} value={formData.description} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="NIP" name="nip" required value={formData.nip} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="REGON *" name="regon" required value={formData.regon} onChange={handleChange} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>{t('onboarding.companyContactSection')}</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('forms.contactEmail')} name="email" required value={formData.email} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('forms.phoneNumber')} name="phone" value={formData.phone} onChange={handleChange} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label={t('forms.websiteUrl')} name="website" value={formData.website} onChange={handleChange} />
              </Grid>
              <Grid item xs={12}>
                <LocationPickerMap
                  lat={parseFloat(formData.lat) || 0}
                  lng={parseFloat(formData.lng) || 0}
                  onLocationSelect={handleLocationSelect}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('forms.city')} name="city" value={formData.city} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('forms.address')} name="address" value={formData.address} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('forms.latitude')} name="lat" value={formData.lat} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('forms.longitude')} name="lng" value={formData.lng} onChange={handleChange} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate('/first-steps')}>{t('common.back')}</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? t('onboarding.saving') : t('onboarding.companyCreate')}
          </Button>
        </Box>
      </Box>
    </Container>
  );
}

// ============================================================
// PILOT ONBOARDING PAGE
// ============================================================

export function PilotOnboardingPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { currentUser, isAuthenticated, isLoading, refreshUser } = useAuthentication();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    nip: '',
    regon: '',
    email: currentUser?.email || '',
    phone: '',
    ibanAccount: '',
    city: '',
    address: '',
    lat: '',
    lng: '',
    permissions: [] as Permission[],
  });

  const handleLocationSelect = useLocationFields(formData, setFormData);

  if (isLoading || (isAuthenticated && !currentUser)) {
    return <LoadingSpinner message={t('auth.checkingAccount')} fullHeight />;
  }

  if (currentUser?.membership) {
    return <AlreadyRegistered />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePermissionChange = (permission: Permission) => {
    const current = [...formData.permissions];
    if (current.includes(permission)) {
      setFormData({ ...formData, permissions: current.filter(p => p !== permission) });
    } else {
      setFormData({ ...formData, permissions: [...current, permission] });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasNonEmptyValue(formData.name) || !hasNonEmptyValue(formData.surname)) {
      setError(t('onboarding.fillName'));
      return;
    }

    const validationError = validateCompanyProfileForm({
      company_name: '',
      nip: formData.nip,
      regon: formData.regon,
      email: formData.email,
      phone: formData.phone,
      website: '',
      lat: formData.lat,
      lng: formData.lng,
    }, t, { requireCompanyName: false });
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (!isValidIban(formData.ibanAccount)) {
        setError(t('notifications.invalidIban'));
        return;
      }

      const payload = {
        personal_data: {
          name: formData.name.trim(),
          surname: formData.surname.trim(),
        },
        ibanAccount: normalizeIban(formData.ibanAccount),
        permissions: formData.permissions,
        company_information: {
          tax_information: {
            tax_identification_number: formData.nip.trim(),
            regon: formData.regon.trim(),
          },
          contact_details: {
            email: formData.email.trim(),
            phone_number: formData.phone.trim(),
            location: {
              city: formData.city.trim(),
              address: formData.address.trim(),
              lat: formData.lat ? parseFloat(formData.lat) : undefined,
              lng: formData.lng ? parseFloat(formData.lng) : undefined,
            }
          },
          verified: false,
          highlighted: false,
        },
        active: true,
      };

      await createPilotProfile(payload);

      await refreshUser();
      navigate('/account');
    } catch (err: any) {
      setError(err.message || t('onboarding.pilotProfileError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Typography variant="h3" gutterBottom fontWeight={700}>{t('onboarding.pilotTitle')}</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>{t('onboarding.pilotSubtitle')}</Typography>

      {error && (
        <Box sx={{ mb: 3 }}>
          <ErrorDisplay message={error} />
        </Box>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>{t('onboarding.pilotPersonalSection')}</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('forms.firstName')} name="name" required value={formData.name} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('forms.lastName')} name="surname" required value={formData.surname} onChange={handleChange} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>{t('onboarding.pilotPermissionsSection')}</Typography>
            <FormGroup row>
              {(['BF2', 'BF3', 'BF4', 'HIPO'] as Permission[]).map((perm) => (
                <FormControlLabel
                  key={perm}
                  control={
                    <Checkbox
                      checked={formData.permissions.includes(perm)}
                      onChange={() => handlePermissionChange(perm)}
                    />
                  }
                  label={perm}
                />
              ))}
            </FormGroup>
          </CardContent>
        </Card>

        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>{t('onboarding.pilotCompanySection')}</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="NIP" name="nip" required value={formData.nip} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="REGON" name="regon" value={formData.regon} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('forms.contactEmail')} name="email" required value={formData.email} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('forms.phoneNumber')} name="phone" value={formData.phone} onChange={handleChange} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label={t('forms.iban')} name="ibanAccount" value={formData.ibanAccount} onChange={handleChange} />
              </Grid>
              <Grid item xs={12}>
                <LocationPickerMap
                  lat={parseFloat(formData.lat) || 0}
                  lng={parseFloat(formData.lng) || 0}
                  onLocationSelect={handleLocationSelect}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('forms.city')} name="city" value={formData.city} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('forms.address')} name="address" value={formData.address} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('forms.latitude')} name="lat" value={formData.lat} onChange={handleChange} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={t('forms.longitude')} name="lng" value={formData.lng} onChange={handleChange} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate('/first-steps')}>{t('common.back')}</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? t('onboarding.saving') : t('onboarding.pilotCreate')}
          </Button>
        </Box>
      </Box>
    </Container>
  );
}
